import { useEffect, useRef, useState } from 'react';
import { getNamespace, disconnectNamespace } from '../utils/socketManager';

/**
 * React hook to subscribe to events on a Socket.IO namespace.
 *
 * The socket itself lives in the module-level singleton (socketManager).
 * This hook only manages event listener registration/cleanup within
 * the React component lifecycle.
 *
 * @param {string} path - Namespace path (e.g., '/contracts')
 * @param {Object} handlers - Map of event names to handler functions.
 *   Handlers are stored in a ref so they can be updated without
 *   re-subscribing. Example: { contracts_update: (data) => {...} }
 * @param {Object} [options]
 * @param {Object} [options.query] - Query params for the namespace handshake
 * @param {boolean} [options.enabled=true] - Whether to connect
 * @param {boolean} [options.disconnectOnCleanup=false] - Disconnect the
 *   namespace when the component unmounts (for dialog-scoped sockets)
 *
 * @example
 * useSocket('/contracts', {
 *   contracts_update: (event) => {
 *     if (['insert', 'update'].includes(event.operationType)) {
 *       refreshGrid();
 *     }
 *   },
 * });
 *
 * @example
 * // Dialog-scoped with query params:
 * useSocket(
 *   '/jobs/training',
 *   {
 *     training_jobs_update: (data) => setJobs(prev => upsert(prev, data)),
 *     training_jobs_polling_update: (data) => setJobs(data),
 *   },
 *   { query: { modelId }, enabled: isOpen, disconnectOnCleanup: true }
 * );
 */
export const useSocket = (path, handlers, options = {}) => {
  const { query, enabled = true, disconnectOnCleanup = false } = options;

  // Always reflect the latest handlers without re-subscribing
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Stable ref for the query to avoid stringify on every render
  const queryKeyRef = useRef(query ? JSON.stringify(query) : '');
  const queryKey = query ? JSON.stringify(query) : '';
  queryKeyRef.current = queryKey;

  useEffect(() => {
    if (!enabled) return;

    const socket = getNamespace(path, query);

    // Build a wrapper map so we can .off() exact references
    const wrappers = {};
    const eventNames = Object.keys(handlersRef.current);

    eventNames.forEach((event) => {
      wrappers[event] = (...args) => handlersRef.current[event]?.(...args);
      socket.on(event, wrappers[event]);
    });

    return () => {
      // Remove only our listeners — other hooks/components may share this socket
      eventNames.forEach((event) => {
        socket.off(event, wrappers[event]);
      });

      if (disconnectOnCleanup) {
        disconnectNamespace(path, query);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, queryKey, disconnectOnCleanup]);
};

/**
 * Convenience hook that returns the namespace socket instance directly.
 * Useful when you need to emit events, not just listen.
 *
 * @param {string} path - Namespace path
 * @param {Object} [options] - { query, enabled }
 * @returns {import('socket.io-client').Socket | null}
 */
export const useSocketManager = (path, options = {}) => {
  const { query, enabled = true } = options;
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setSocket(null);
      return;
    }
    setSocket(getNamespace(path, query));
    return () => {
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, query ? JSON.stringify(query) : '']);

  return socket;
};
